import React, { useState, useEffect } from 'react';
import { FaFilm, FaHeart, FaBookmark, FaSpinner } from 'react-icons/fa';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const SimpleMovieCalendar = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [calendarData, setCalendarData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { isDark } = useTheme();

    useEffect(() => {
        const fetchCalendarData = async () => {
            try {
                setLoading(true);

                // Fetch user's favorites and watchlist - handle missing endpoints gracefully
                const [favoritesRes, watchlistRes] = await Promise.all([
                    api.get('/user/favorites').catch(() => ({ data: { favorites: [] } })),
                    api.get('/user/watchlist').catch(() => ({ data: { watchlist: [] } }))
                ]);

                const favorites = favoritesRes.data.favorites || [];
                const watchlist = watchlistRes.data.watchlist || [];

                // Simulate adding dates for demonstration
                const data = {};

                // Add some sample data for the current month
                const today = new Date();
                const startOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                const endOfCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

                // Generate days in month
                const daysInMonth = [];
                for (let d = new Date(startOfCurrentMonth); d <= endOfCurrentMonth; d.setDate(d.getDate() + 1)) {
                    daysInMonth.push(new Date(d));
                }

                // Simulate adding movies on random days
                daysInMonth.forEach((day, index) => {
                    if (Math.random() > 0.7) { // 30% chance of having movies on a day
                        const dayKey = formatDate(day);
                        data[dayKey] = {
                            favorites: Math.floor(Math.random() * 3),
                            watchlist: Math.floor(Math.random() * 2),
                            total: 0
                        };
                        data[dayKey].total = data[dayKey].favorites + data[dayKey].watchlist;
                    }
                });

                setCalendarData(data);
            } catch (err) {
                console.error('Failed to fetch calendar data:', err);
                setError('Failed to load calendar data');
            } finally {
                setLoading(false);
            }
        };

        fetchCalendarData();
    }, [currentDate]);

    // Helper function to format date
    const formatDate = (date) => {
        return date.getFullYear() + '-' +
            String(date.getMonth() + 1).padStart(2, '0') + '-' +
            String(date.getDate()).padStart(2, '0');
    };

    // Helper function to get month start
    const getMonthStart = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    };

    // Helper function to get month end
    const getMonthEnd = (date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    };

    // Helper function to get calendar start (including previous month days)
    const getCalendarStart = (monthStart) => {
        const start = new Date(monthStart);
        const dayOfWeek = start.getDay();
        start.setDate(start.getDate() - dayOfWeek);
        return start;
    };

    // Helper function to get calendar end (including next month days)
    const getCalendarEnd = (monthEnd) => {
        const end = new Date(monthEnd);
        const dayOfWeek = end.getDay();
        end.setDate(end.getDate() + (6 - dayOfWeek));
        return end;
    };

    // Helper function to generate calendar days
    const getCalendarDays = (start, end) => {
        const days = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            days.push(new Date(d));
        }
        return days;
    };

    const navigateMonth = (direction) => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            if (direction === 'next') {
                newDate.setMonth(newDate.getMonth() + 1);
            } else {
                newDate.setMonth(newDate.getMonth() - 1);
            }
            return newDate;
        });
    };

    const getDayData = (day) => {
        const dayKey = formatDate(day);
        return calendarData[dayKey] || { favorites: 0, watchlist: 0, total: 0 };
    };

    const getDayIntensity = (total) => {
        if (total === 0) return 'bg-gray-100 dark:bg-gray-800';
        if (total === 1) return 'bg-green-200 dark:bg-green-800';
        if (total === 2) return 'bg-green-300 dark:bg-green-700';
        if (total === 3) return 'bg-green-400 dark:bg-green-600';
        return 'bg-green-500 dark:bg-green-500';
    };

    const isToday = (day) => {
        const today = new Date();
        return day.toDateString() === today.toDateString();
    };

    const formatMonthYear = (date) => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return months[date.getMonth()] + ' ' + date.getFullYear();
    };

    if (loading) {
        return (
            <div className="py-12">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <FaSpinner className="text-4xl animate-spin mx-auto mb-4 text-purple-600" />
                        <p className="theme-text-secondary">Loading your movie calendar...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-12">
                <div className="text-center">
                    <p className="text-red-600 text-lg">{error}</p>
                </div>
            </div>
        );
    }

    const monthStart = getMonthStart(currentDate);
    const monthEnd = getMonthEnd(currentDate);
    const calendarStart = getCalendarStart(monthStart);
    const calendarEnd = getCalendarEnd(monthEnd);
    const calendarDays = getCalendarDays(calendarStart, calendarEnd);

    return (
        <div className="py-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h2 className="text-3xl font-bold theme-text-primary mb-2">
                        📅 Your Movie Calendar
                    </h2>
                    <p className="theme-text-secondary">
                        Track when you added movies to your collections
                    </p>
                </div>

                {/* Calendar */}
                <div className="theme-bg-secondary rounded-xl p-6 theme-shadow-lg theme-border border">
                    {/* Calendar Header */}
                    <div className="flex items-center justify-between mb-6">
                        <button
                            onClick={() => navigateMonth('prev')}
                            className="p-2 rounded-lg theme-bg-tertiary hover:theme-bg-primary transition-colors"
                        >
                            <svg className="w-5 h-5 theme-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <h3 className="text-xl font-bold theme-text-primary">
                            {formatMonthYear(currentDate)}
                        </h3>

                        <button
                            onClick={() => navigateMonth('next')}
                            className="p-2 rounded-lg theme-bg-tertiary hover:theme-bg-primary transition-colors"
                        >
                            <svg className="w-5 h-5 theme-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1 mb-4">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} className="p-2 text-center text-sm font-semibold theme-text-tertiary">
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                        {calendarDays.map((day, index) => {
                            const dayData = getDayData(day);
                            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                            const isCurrentDay = isToday(day);

                            return (
                                <div
                                    key={index}
                                    className={`
                    aspect-square p-2 rounded-lg border-2 transition-all duration-200 cursor-pointer
                    ${isCurrentMonth ? 'theme-text-primary' : 'theme-text-tertiary'}
                    ${isCurrentDay ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'theme-border'}
                    ${getDayIntensity(dayData.total)}
                    hover:scale-105 hover:shadow-md
                  `}
                                    title={`${day.toLocaleDateString()} - ${dayData.total} movies added`}
                                >
                                    <div className="flex flex-col h-full">
                                        <div className="text-sm font-medium mb-1">
                                            {day.getDate()}
                                        </div>

                                        {dayData.total > 0 && (
                                            <div className="flex-1 flex flex-col justify-center items-center space-y-1">
                                                {dayData.favorites > 0 && (
                                                    <div className="flex items-center space-x-1">
                                                        <FaHeart className="text-xs text-red-500" />
                                                        <span className="text-xs font-bold">{dayData.favorites}</span>
                                                    </div>
                                                )}
                                                {dayData.watchlist > 0 && (
                                                    <div className="flex items-center space-x-1">
                                                        <FaBookmark className="text-xs text-blue-500" />
                                                        <span className="text-xs font-bold">{dayData.watchlist}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Legend */}
                    <div className="mt-6 pt-6 border-t theme-border">
                        <div className="flex flex-wrap items-center justify-center space-x-6 text-sm">
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 bg-gray-100 dark:bg-gray-800 rounded"></div>
                                <span className="theme-text-secondary">No movies</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 bg-green-200 dark:bg-green-800 rounded"></div>
                                <span className="theme-text-secondary">1 movie</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 bg-green-300 dark:bg-green-700 rounded"></div>
                                <span className="theme-text-secondary">2 movies</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 bg-green-400 dark:bg-green-600 rounded"></div>
                                <span className="theme-text-secondary">3 movies</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 bg-green-500 rounded"></div>
                                <span className="theme-text-secondary">4+ movies</span>
                            </div>
                        </div>

                        <div className="flex items-center justify-center space-x-6 mt-4 text-sm">
                            <div className="flex items-center space-x-2">
                                <FaHeart className="text-red-500" />
                                <span className="theme-text-secondary">Favorites</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <FaBookmark className="text-blue-500" />
                                <span className="theme-text-secondary">Watchlist</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                    <div className="theme-bg-secondary rounded-xl p-6 theme-shadow-lg theme-border border text-center">
                        <FaFilm className="text-3xl text-purple-500 mx-auto mb-3" />
                        <h3 className="text-2xl font-bold theme-text-primary mb-1">
                            {Object.values(calendarData).reduce((sum, day) => sum + day.total, 0)}
                        </h3>
                        <p className="theme-text-secondary">Total Movies Added</p>
                    </div>

                    <div className="theme-bg-secondary rounded-xl p-6 theme-shadow-lg theme-border border text-center">
                        <FaHeart className="text-3xl text-red-500 mx-auto mb-3" />
                        <h3 className="text-2xl font-bold theme-text-primary mb-1">
                            {Object.values(calendarData).reduce((sum, day) => sum + day.favorites, 0)}
                        </h3>
                        <p className="theme-text-secondary">Favorites Added</p>
                    </div>

                    <div className="theme-bg-secondary rounded-xl p-6 theme-shadow-lg theme-border border text-center">
                        <FaBookmark className="text-3xl text-blue-500 mx-auto mb-3" />
                        <h3 className="text-2xl font-bold theme-text-primary mb-1">
                            {Object.values(calendarData).reduce((sum, day) => sum + day.watchlist, 0)}
                        </h3>
                        <p className="theme-text-secondary">Watchlist Added</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SimpleMovieCalendar;
